import React from 'react';
import {Image, ImageAspectRatioPresets, TextButton, TextButtonPriority} from '@wix/wix-ui-tpa/cssVars';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {useLocaleKeys} from '../../../locale-keys/LocaleKeys';
import {classes} from './StoreNameAndLogo.st.css';
import classNames from 'clsx';
import {CheckoutHeaderModel} from '../../../domain/models/common/CheckoutHeader.model';
import {SPECS} from '../../constants';

export interface StoreNameAndLogoProps {
  continueShoppingUrl?: string;
  clickOnContinueShopping?: () => void;
  logoUrl?: string;
  storeName?: string;
  checkoutHeader?: CheckoutHeaderModel;
  isOneColumnView?: boolean;
}

export enum StoreNameAndLogoDataHooks {
  root = 'StoreNameAndLogoDataHooks.root',
  logo = 'StoreNameAndLogoDataHooks.logo',
  storeName = 'StoreNameAndLogoDataHooks.storeName',
  title = 'StoreNameAndLogoDataHooks.title',
}

export enum Alignment {
  SIDE = 'SIDE',
  CENTER = 'CENTER',
}

export enum LogoSize {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

export const StoreNameAndLogo = ({
  continueShoppingUrl,
  clickOnContinueShopping,
  logoUrl,
  storeName,
  checkoutHeader,
  isOneColumnView,
}: // eslint-disable-next-line sonarjs/cognitive-complexity
StoreNameAndLogoProps) => {
  const localKeys = useLocaleKeys();
  const {experiments} = useExperiments();
  const shouldApplyHeaderCustomizations = experiments.enabled(SPECS.CheckoutHeaderAdditionalCustomizationsCheckout);

  const shouldUseCheckoutHeaderOnCheckout = !!checkoutHeader;
  const shouldShowLogo = !!checkoutHeader?.logo?.size;
  const shouldShowStoreName = !shouldShowLogo;
  const alignment = checkoutHeader?.alignment;
  const shouldDisplayCheckoutLabel = checkoutHeader?.displayCheckoutLabel;
  const logoSize = checkoutHeader?.logo?.size;
  const text = checkoutHeader?.text;
  const titleText = shouldApplyHeaderCustomizations && text ? text : storeName;

  const logoHeight = () => {
    /* istanbul ignore else */
    /* istanbul ignore next */
    if (logoSize === LogoSize.MEDIUM) {
      return isOneColumnView ? 56 : 80;
    }
    /* istanbul ignore next */
    if (logoSize === LogoSize.LARGE) {
      return isOneColumnView ? 80 : 148;
    }
    /* istanbul ignore next */
    return isOneColumnView ? 26 : 56;
  };

  const checkoutTitle = () => {
    if ((!titleText && shouldShowStoreName) || (!logoUrl && shouldShowLogo)) {
      return localKeys.checkout.header_default_title();
    }
    if (shouldShowLogo && !shouldDisplayCheckoutLabel) {
      return undefined;
    }
    return localKeys.checkout.header.store_title_suffix();
  };

  const storeNameAndLogoTitle =
    titleText || logoUrl ? localKeys.checkout.header.store_title_suffix() : localKeys.checkout.header_default_title();

  return shouldUseCheckoutHeaderOnCheckout ? (
    <div
      data-hook={StoreNameAndLogoDataHooks.root}
      className={classNames(
        alignment === Alignment.CENTER
          ? /* istanbul ignore next */ classes.headerTitleAndLogoCenter
          : classes.headerTitleAndLogoSide,
        classes.headerTitleAndLogoContent
      )}>
      {shouldShowLogo && (
        <a
          onClick={() => clickOnContinueShopping?.()}
          href={continueShoppingUrl}
          data-hook={StoreNameAndLogoDataHooks.logo}>
          <Image
            src={logoUrl}
            width={logoHeight()}
            height={logoHeight()}
            alt={localKeys.checkout.header.businessLogo.altText({storeName})}
          />
        </a>
      )}
      {shouldShowStoreName && (
        <TextButton
          as={'a'}
          priority={TextButtonPriority.primary}
          onClick={() => clickOnContinueShopping?.()}
          href={continueShoppingUrl}
          data-hook={StoreNameAndLogoDataHooks.storeName}
          className={classes.headerStoreName}>
          {titleText}
        </TextButton>
      )}
      <h1
        className={classes.headerTitle}
        data-hook={StoreNameAndLogoDataHooks.title}
        aria-label={checkoutTitle() || storeNameAndLogoTitle}>
        {checkoutTitle()}
      </h1>
    </div>
  ) : (
    <div
      data-hook={StoreNameAndLogoDataHooks.root}
      className={classNames(classes.headerTitleAndLogo, classes.headerTitleAndLogoContent)}>
      {logoUrl && (
        <a
          onClick={() => clickOnContinueShopping?.()}
          href={continueShoppingUrl}
          data-hook={StoreNameAndLogoDataHooks.logo}
          className={classes.logo}>
          <Image
            src={logoUrl}
            aspectRatio={ImageAspectRatioPresets.square}
            fluid={true}
            alt={localKeys.checkout.header.businessLogo.altText({storeName})}
          />
        </a>
      )}
      {storeName && !logoUrl && (
        <TextButton
          as={'a'}
          priority={TextButtonPriority.primary}
          onClick={() => clickOnContinueShopping?.()}
          href={continueShoppingUrl}
          data-hook={StoreNameAndLogoDataHooks.storeName}
          className={classes.headerStoreName}>
          {storeName}
        </TextButton>
      )}
      <h1
        className={classes.headerTitle}
        data-hook={StoreNameAndLogoDataHooks.title}
        aria-label={storeNameAndLogoTitle}>
        {storeNameAndLogoTitle}
      </h1>
    </div>
  );
};
